import { useState, useCallback, useEffect, useMemo } from 'react';
import { ProfileData, FormatType } from '../types';
import { profileToMarkdown, markdownToProfile } from '../utils/markdownTransform';
import { debounce } from 'lodash';

interface HistoryEntry {
  profiles: ProfileData[];
  currentIndex: number;
  formatType: FormatType;
  timestamp: number;
}

interface UseProfileEditorProps {
  profiles: ProfileData[];
  currentIndex: number;
  formatType: FormatType;
  onProfileChange: (index: number, profile: ProfileData) => void;
  onNavigate: (index: number) => void;
  onFormatChange: (format: FormatType) => void;
  autoSaveInterval?: number;
}

export const useProfileEditor = ({
  profiles,
  currentIndex,
  formatType,
  onProfileChange,
  onNavigate,
  onFormatChange,
  autoSaveInterval = 30000 // 30 seconds
}: UseProfileEditorProps) => {
  // Simple undo/redo with max 10 entries
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [currentMarkdown, setCurrentMarkdown] = useState('');
  
  const maxHistorySize = 10;

  // Initialize markdown content
  useEffect(() => {
    if (profiles[currentIndex]) {
      const markdown = profileToMarkdown(profiles[currentIndex], formatType);
      setCurrentMarkdown(markdown);
    }
  }, [profiles, currentIndex, formatType]);

  // Add history entry
  const addHistoryEntry = useCallback(() => {
    const entry: HistoryEntry = {
      profiles: [...profiles],
      currentIndex,
      formatType,
      timestamp: Date.now()
    };

    setHistory(prev => {
      // Remove entries after current index if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(entry);
      
      // Keep only last 10 entries
      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }
      
      return newHistory;
    });

    setHistoryIndex(prev => Math.min(prev + 1, maxHistorySize - 1));
    setIsDirty(true);
  }, [profiles, currentIndex, formatType, historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const prevEntry = history[historyIndex - 1];
      
      // Restore previous state
      prevEntry.profiles.forEach((profile, index) => {
        onProfileChange(index, profile);
      });
      
      if (prevEntry.currentIndex !== currentIndex) {
        onNavigate(prevEntry.currentIndex);
      }
      
      if (prevEntry.formatType !== formatType) {
        onFormatChange(prevEntry.formatType);
      }
      
      setHistoryIndex(historyIndex - 1);
      setIsDirty(historyIndex > 1);
    }
  }, [history, historyIndex, currentIndex, formatType, onProfileChange, onNavigate, onFormatChange]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextEntry = history[historyIndex + 1];
      
      // Restore next state
      nextEntry.profiles.forEach((profile, index) => {
        onProfileChange(index, profile);
      });
      
      if (nextEntry.currentIndex !== currentIndex) {
        onNavigate(nextEntry.currentIndex);
      }
      
      if (nextEntry.formatType !== formatType) {
        onFormatChange(nextEntry.formatType);
      }
      
      setHistoryIndex(historyIndex + 1);
      setIsDirty(true);
    }
  }, [history, historyIndex, currentIndex, formatType, onProfileChange, onNavigate, onFormatChange]);

  // Handle markdown change
  const handleMarkdownChange = useCallback((markdown: string) => {
    setCurrentMarkdown(markdown);
    setIsDirty(true);
    
    // Parse markdown and update profile data
    if (profiles[currentIndex]) {
      const updatedProfile = markdownToProfile(markdown, formatType, profiles[currentIndex]);
      onProfileChange(currentIndex, updatedProfile);
      
      // Add to history after a short delay to avoid too many entries
      setTimeout(addHistoryEntry, 500);
    }
  }, [profiles, currentIndex, formatType, onProfileChange, addHistoryEntry]);

  // Handle format change with edit preservation
  const handleFormatChange = useCallback((newFormat: FormatType) => {
    if (newFormat !== formatType && profiles[currentIndex]) {
      // Convert current markdown to profile data first
      const currentProfile = markdownToProfile(currentMarkdown, formatType, profiles[currentIndex]);
      
      // Update profile with latest changes
      onProfileChange(currentIndex, currentProfile);
      
      // Change format
      onFormatChange(newFormat);
      
      // Update markdown for new format
      const newMarkdown = profileToMarkdown(currentProfile, newFormat);
      setCurrentMarkdown(newMarkdown);
      
      addHistoryEntry();
    }
  }, [formatType, profiles, currentIndex, currentMarkdown, onProfileChange, onFormatChange, addHistoryEntry]);

  // Manual save
  //
  // DAG-003: in-progress edits used to be written to localStorage as raw
  // profile JSON every 30 seconds (and on navigation). That kept governed
  // employee bios on the endpoint for up to 7 days. We now only track the
  // "saved" timestamp in component state; persistence happens server-side
  // through the proposal update endpoint, which is access-controlled.
  const saveNow = useCallback(async () => {
    if (!isDirty) return;

    setIsAutoSaving(true);

    try {
      setLastSaved(new Date());
      setIsDirty(false);
    } catch (error) {
      console.error('Failed to mark draft saved:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [isDirty]);

  // Auto-save with debouncing
  const debouncedAutoSave = useMemo(
    () => debounce(() => {
      if (isDirty) {
        saveNow();
      }
    }, autoSaveInterval),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoSaveInterval]
  );

  // Trigger auto-save when dirty
  useEffect(() => {
    if (isDirty) {
      debouncedAutoSave();
    }
    
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [isDirty, debouncedAutoSave]);

  // Navigation with auto-save
  const handleNavigation = useCallback((newIndex: number) => {
    // Save current changes before navigating
    if (isDirty) {
      saveNow();
    }
    
    onNavigate(newIndex);
  }, [isDirty, saveNow, onNavigate]);

  // DAG-003: no localStorage draft to load. Stale bulk drafts written by
  // older builds are swept by draftStorage.cleanup() on app startup.

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  return {
    // Editor state
    currentMarkdown,
    isDirty,
    isAutoSaving,
    lastSaved,
    
    // History controls
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    undo,
    redo,
    
    // Handlers
    handleMarkdownChange,
    handleFormatChange,
    handleNavigation,
    saveNow,
    
    // Status
    saveStatus: isAutoSaving 
      ? 'Saving...' 
      : lastSaved 
        ? `Saved ${formatTimeAgo(lastSaved)}` 
        : 'Not saved'
  };
};

// Helper function to format time ago
function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default useProfileEditor;